import { PartnerNumericAgreementForm } from "@/forms/partner-numeric-agreement-form/partner-numeric-agreement-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { Alert } from "@msuite/ui/alert";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import {
	getPartnerAgreementTypeTranslationKey,
	getPartnerAgreementTypeUnit,
} from "@msuite/utils/partnerships";
import {
	type PatchPartnershipNumericAgreementSchema,
	patchPartnershipNumericAgreementSchema,
} from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import type { Enums, Tables } from "@msuite/utils/types";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditNumericAgreementModalProps extends ModalProps {
	history: Tables<"organizationPartnershipAgreements">[];
	type: Enums<"organizationPartnershipAgreementType">;
	orgId: string;
	partnershipId: string;
	onCompleteEdit: () => Promise<void>;
	agreementId: string | undefined;
}

export function EditNumericAgreementModal({
	isOpen,
	onClose,
	type,
	orgId,
	partnershipId,
	onCompleteEdit,
	history,
	agreementId,
}: EditNumericAgreementModalProps) {
	/** Constants */
	const staticUnit = getPartnerAgreementTypeUnit(type);

	/** Hooks */
	const { organization } = useAuth();
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		if (!organization) return {} as PatchPartnershipNumericAgreementSchema;
		const response = await client.organizations[":orgId"].partnerships[":partnershipId"].agreements[
			":agreementType"
		].$get({
			param: {
				orgId: guardString(organization.id),
				partnershipId,
				agreementType: type,
			},
			query: {
				agreementId,
			},
		});

		const data = await responseHandler(response);
		if (!data.active) {
			return {
				startDate: moment().postgresTimestamp(),
				unit: staticUnit,
			} as PatchPartnershipNumericAgreementSchema;
		}

		if (agreementId) {
			const focusedAgreement = history.find((agreement) => agreement.id === agreementId);
			if (focusedAgreement) {
				return focusedAgreement as PatchPartnershipNumericAgreementSchema;
			}
		}

		return data.active as PatchPartnershipNumericAgreementSchema;
	});
	const methods = useForm({
		resolver: resolver(patchPartnershipNumericAgreementSchema),
		defaultValues,
	});
	const validFrom = useWatch({ control: methods.control, name: "startDate" });
	const isValidFromTaken = history.some(
		(agreement) => agreement.startDate === validFrom && agreement.id !== agreementId,
	);

	/** Functions */
	async function onSubmit(json: PatchPartnershipNumericAgreementSchema) {
		await client.organizations[":orgId"].partnerships[":partnershipId"]["numeric-agreements"][
			":agreementType"
		].$post({
			param: {
				orgId,
				partnershipId,
				agreementType: type,
			},
			query: {
				agreementId,
			},
			json,
		});
		await onCompleteEdit();
		handleOnClose();
	}

	function handleOnClose() {
		onClose();
		methods.reset();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={handleOnClose}
				title={t(getPartnerAgreementTypeTranslationKey(type))}
				onSubmit={methods.handleSubmit(onSubmit, (error) => console.error(error))}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={handleOnClose} />
				) : (
					<>
						<Modal.Body>
							{isValidFromTaken && (
								<Alert
									variant="danger"
									spacing="compact"
									title={t("partners.agreement_date_taken")}
									description={t("partners.agreement_date_taken_description")}
									onClick={() => {}}
								/>
							)}
							{staticUnit && (
								<PartnerNumericAgreementForm
									type={type}
									staticUnit={staticUnit}
								/>
							)}
						</Modal.Body>
						<Modal.FooterForm isDisabled={isValidFromTaken} />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
